import React, {useEffect, useState} from 'react';
import Wrapper from '../../components/Wrapper';
import DashboardHeader from '../../components/DashboardHeader';
import './index.scss';
import Input from '../../components/Input';
import {
  useGetEquipmentTypeQuery,
  useGetFieldTypesQuery,
  useLazyGetEquipmentTypeQuery,
} from '../../store/builder/inspections.api';
import {useDispatch, useSelector} from 'react-redux';
import {setLoader} from '../../store/builder/reducer';
import {useNavigate, useLocation, useParams} from 'react-router-dom';
import {useErrorHandler} from '../../global/ErrorsHandler';
import {Helmet} from 'react-helmet';
import Select from 'react-select';
import SelectField from '../../components/EquipmentComponents/SelectField';
import {useGetRevisions} from './model/revisions/getRevisions';
import {useSetCurrentRevision} from './model/revisions/setCurrentRevision';
import useLoader from '../../global/UseLoadingHandler';
import ROUTES from '../../utils/routes';
import {useTranslation} from '../../hooks/useTranslation';

const SELECT_STYLED = {
  control: (baseStyles, state) => ({
    ...baseStyles,
    borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
    borderRadius: 8,
    height: 45,
    backgroundColor: '#F4F5F6',
    marginTop: 8,
  }),
};

export default function EquipmentType() {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [selectedOption, setSelectedOption] = useState(null);
  const [getRevisions, revisionOptions, current_revision] = useGetRevisions();
  const [handleSelect] = useSetCurrentRevision();
  const fromCustomEquipment = window.location.pathname === ROUTES.private.customEquipmentType;

  const getFilterOptions = () => {
    if (fromCustomEquipment) {
      return revisionOptions.filter(el => el.code === '');
    } else {
      return revisionOptions.filter(el => el.code !== '');
    }
  };

  const [
    getEquipmentType,
    {
      data: equipmentTypesData,
      isError: equipmentTypesIsError,
      error: equipmentTypesError,
      isSuccess: equipmentTypesIsSuccess,
      isFetching: equipmentTypesIsLoading,
    },
  ] = useLazyGetEquipmentTypeQuery();

  const [errorHandler, setErrorHandler] = useErrorHandler([
    equipmentTypesError,
  ]);

  useEffect(() => {
    getRevisions();
  }, []);

  const filteredOptions = getFilterOptions();

  useEffect(() => {
    if (filteredOptions.length > 0) {
      setSelectedOption(filteredOptions[0]);
      handleSelect(filteredOptions[0]);
    } else {
      setSelectedOption(null);
    }
  }, [fromCustomEquipment, revisionOptions]);

  const handleChange = selected => {
    setSelectedOption(selected);
    handleSelect(selected);
  };

  useEffect(() => {
    dispatch(setLoader(true));
    getEquipmentType({revision: current_revision?.code})
      .unwrap()
      .then(res => {
        dispatch(setLoader(false));
      })
      .catch(err => {
        dispatch(setLoader(false));
      });
  }, [current_revision, fromCustomEquipment]);

  useLoader([equipmentTypesIsLoading]);

  const filteredData = equipmentTypesData?.filter(el => {
    if (search === '') {
      return el;
    } else {
      return el?.title?.toLowerCase()?.includes(search.toLowerCase());
    }
  });

  const handleSearch = e => {
    setSearch(e.target.value);
  };

  return (
    <Wrapper>
      <Helmet>
        <title>{fromCustomEquipment ? t('equipmentType.customPageTitle') : t('equipmentType.pageTitle')}</title>
      </Helmet>
      <DashboardHeader
        title={fromCustomEquipment ? t('equipmentType.customTitle') : t('equipmentType.title')}
        type={'equipment-type'}
        showBtn={!fromCustomEquipment}
        action={() =>
          navigate(
            '/equipment-type/assigned' + `?revision=${current_revision?.code}`,
          )
        }
      />
      <div className='equipmentType'>
        <div className='equipmentType__content'>
          <div className='equipmentType__search'>
            <Input
              type='text'
              placeholder={t('equipmentType.searchPlaceholder')}
              name='equipmentTypeSearch'
              id='equipmentTypeSearch'
              image={'/assets/icons/search.svg'}
              maxwidth={264}
              onChange={handleSearch}
            />
            <div className='equipmentType__field' style={{width: '338px'}}>
              <Select
                options={getFilterOptions()}
                styles={SELECT_STYLED}
                value={selectedOption}
                onChange={handleChange}
                isDisabled={fromCustomEquipment}
              />
            </div>
          </div>

          <div className='equipmentType__table'>
            <div className='equipmentType__table-header'>
              <div className='equipmentType__th'>{t('equipmentType.tableHeaders.equipmentType')}</div>
              <div className='equipmentType__th'>{t('equipmentType.tableHeaders.inspections')}</div>
              <div className='equipmentType__th'></div>
            </div>
            <div className='equipmentType__table-body'>
              {filteredData?.length ? (
                filteredData?.map(type => (
                  <div className='equipmentType__row' key={type.id}>
                    <div className='equipmentType__row-th'>{type.title}</div>
                    <div className='equipmentType__row-th'>
                      {type.links.length}
                    </div>
                    <div className='equipmentType__row-th'>
                      {
                        !fromCustomEquipment && (
                          <div
                            onClick={() =>
                              navigate(
                                '/equipment-type/edit/' +
                                  type.id +
                                  `?revision=${current_revision?.code}`,
                              )
                            }
                          >
                            {t('equipmentType.actions.edit')}
                          </div>
                        )
                      }
                    </div>
                  </div>
                ))
              ) : (
                <div className='emptyList'>
                  <img src='/assets/empty-list.png' alt='empty' />
                  <p>{t('equipmentType.messages.noData')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Wrapper>
  );
}
